import { Pencil, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormSection } from '@components/form-section';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { toast } from '@core/toast';
import { ProductState, VendorDetailsSupportDto } from '@api/WalletClient';
import { LocalizedTextDisplay } from '../../components/localized-text-display';
import { Tooltip } from '../../components/tooltip';
import { getCdnImage } from '../../components/utils/cdn-image';
import { Api } from '../../core/api';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { ImageRequirements } from '../components/image-requirements';
import { StateSummary } from '../components/state-summary';

interface VendorInfoSectionProps {
    vendor: VendorDetailsSupportDto;
    onToggleState: (newState: ProductState) => Promise<void>;
    loading: boolean;
    onImageUpdate: () => void;
}

const InfoField = ({
    label,
    tooltip,
    children
}: {
    label: string;
    tooltip?: string;
    children: React.ReactNode;
}) => (
    <div>
        <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">{label}</span>
            {tooltip && <Tooltip title={tooltip} />}
        </div>
        <div className="text-sm">{children}</div>
    </div>
);

export const VendorInfoSection: React.FC<VendorInfoSectionProps> = ({
    vendor,
    onToggleState,
    loading,
    onImageUpdate
}) => {
    const navigate = useNavigate();
    const { walletClient } = injectTSDI(Api);

    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleImageSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        const maxSizeBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setImageUploadLoading(true);

        try {
            await walletClient.supportApi.vendorSupportControllerReplaceVendorImage(
                vendor.id,
                { file }
            );

            onImageUpdate();
        } catch (error) {
            console.error('Image upload failed:', error);
            toast.error('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    const hasDescriptions = Boolean(
        vendor.shortDescription ||
            vendor.description ||
            vendor.redemptionDescription ||
            vendor.notAvailableReason
    );

    return (
        <div className="flex flex-col gap-6">
            <FormSection
                title="Vendor Details"
                actions={
                    <Button
                        onClick={() => navigate(links.editVendor(vendor.id))}
                    >
                        <Pencil />
                        Edit
                    </Button>
                }
            >
                <div className="grid gap-6 md:grid-cols-12">
                    <div className="flex flex-col gap-3 md:col-span-5">
                        {vendor.image && (
                            <>
                                <div
                                    className="relative w-full overflow-hidden rounded-lg border bg-muted pt-[56.25%]"
                                    title="Vendor image should be in 16:9 aspect ratio for optimal display"
                                >
                                    <img
                                        src={getCdnImage(
                                            vendor.image.bucketName,
                                            vendor.image.objectKey,
                                            {
                                                resize: {
                                                    width: 800,
                                                    height: 450
                                                }
                                            }
                                        )}
                                        alt={vendor.name}
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex-1">
                                        <ImageRequirements
                                            aspectRatio="16:9 (e.g., 1920×1080, 1280×720)"
                                            minimumSize="800×450 pixels"
                                        />
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                    <Button
                                        variant="outline"
                                        loading={imageUploadLoading}
                                        onClick={handleImageSelect}
                                    >
                                        <Upload />
                                        {imageUploadLoading
                                            ? 'Uploading...'
                                            : 'Select New Image'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex flex-col gap-4 md:col-span-7">
                        <div>
                            <h3 className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
                                Basic Information
                            </h3>
                            <div className="mt-2 grid gap-4 border-t pt-3 sm:grid-cols-3">
                                <InfoField label="Name">
                                    <span className="font-medium">
                                        {vendor.name}
                                    </span>
                                </InfoField>
                                <InfoField label="Source">
                                    {vendor.source}
                                </InfoField>
                                <InfoField label="Type">
                                    {vendor.type}
                                </InfoField>
                                {vendor.externalId && (
                                    <InfoField label="External ID">
                                        {vendor.externalId}
                                    </InfoField>
                                )}
                                {vendor.averageDeliveryTime && (
                                    <InfoField label="Avg. Delivery Time">
                                        {vendor.averageDeliveryTime}
                                    </InfoField>
                                )}
                                {vendor.lastSyncDateTime && (
                                    <InfoField label="Last Sync">
                                        {new Date(
                                            vendor.lastSyncDateTime
                                        ).toLocaleString()}
                                    </InfoField>
                                )}
                                <InfoField
                                    label="Override"
                                    tooltip="Properties can be updated by third party voucher provider"
                                >
                                    <Badge
                                        variant={
                                            vendor.overrideEnabled
                                                ? 'success'
                                                : 'muted'
                                        }
                                    >
                                        {vendor.overrideEnabled
                                            ? 'ENABLED'
                                            : 'DISABLED'}
                                    </Badge>
                                </InfoField>
                            </div>
                        </div>

                        <InfoField label="Supported Countries">
                            <div className="mt-1 flex flex-wrap gap-1">
                                {vendor.countries.map((country) => (
                                    <Badge key={country} variant="outline">
                                        {country}
                                    </Badge>
                                ))}
                            </div>
                        </InfoField>

                        {vendor.categories && vendor.categories.length > 0 && (
                            <InfoField label="Categories">
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {vendor.categories.map((category) => (
                                        <Badge
                                            key={category.id}
                                            variant="outline"
                                        >
                                            {category.name.en || category.key}
                                        </Badge>
                                    ))}
                                </div>
                            </InfoField>
                        )}

                        {hasDescriptions && (
                            <div>
                                <h3 className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
                                    Descriptions
                                </h3>
                                <div className="mt-2 flex flex-col gap-3 border-t pt-3">
                                    {vendor.shortDescription && (
                                        <LocalizedTextDisplay
                                            label="Short Description"
                                            value={vendor.shortDescription}
                                        />
                                    )}
                                    {vendor.description && (
                                        <LocalizedTextDisplay
                                            label="Full Description"
                                            value={vendor.description}
                                        />
                                    )}
                                    {vendor.redemptionDescription && (
                                        <LocalizedTextDisplay
                                            label="Redemption Instructions"
                                            value={vendor.redemptionDescription}
                                        />
                                    )}
                                    {vendor.notAvailableReason && (
                                        <LocalizedTextDisplay
                                            label="Not Available Reason"
                                            value={vendor.notAvailableReason}
                                            color="error"
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </FormSection>

            <FormSection title="State Information">
                <StateSummary
                    internalState={vendor.internalState}
                    externalState={vendor.externalState}
                    shopState={vendor.shopState}
                    loading={loading}
                    onToggleState={onToggleState}
                />
            </FormSection>
        </div>
    );
};
